import React, { useEffect, useMemo, useState } from 'react';
import AdminShell from '../../components/layout/AdminShell';
import { adminNavigation } from '../../constants/navigation';
import { api } from '../../lib/supabase';
import { slugify } from '../../lib/utils';
import {
  ActivityType,
  InputConfig,
  InputConfigType,
  NotifyRole,
  OnboardingStep,
  PriorityLevel,
  StepCategoryRecord,
  TargetAudience,
  activityTypeCopy,
  notifyRoleLabels,
  priorityColors,
} from '../../types';
import {
  Bars3BottomLeftIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TableCellsIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { DndContext, PointerSensor, DragEndEvent, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const activityColors: Record<ActivityType, string> = {
  task: 'bg-[#e6f4ff] text-lumin-primary',
  form: 'bg-[#e0f2ff] text-[#0f4d64]',
  document_upload: 'bg-[#e7fbf2] text-[#0f704d]',
  training: 'bg-[#f5ebff] text-[#7c3aed]',
  meeting: 'bg-[#e4f3ff] text-[#0f5f8d]',
  call: 'bg-[#fff0e5] text-[#c2410c]',
  payment: 'bg-[#fff5e5] text-[#b45309]',
  signature: 'bg-[#ffe8f0] text-[#be185d]',
  video: 'bg-[#e9edff] text-[#4338ca]',
  custom: 'bg-[#f3f4f6] text-slate-600',
};

const targetCopy: Record<TargetAudience, string> = {
  loan_officer: 'Loan Officer',
  admin: 'Admin',
  va: 'VA',
};

interface CategoryFormState {
  name: string;
  slug: string;
  description: string;
  color: string;
  accent_color: string;
  icon: string;
  is_active: boolean;
}

interface StepFormState {
  task_name: string;
  category: string;
  priority: PriorityLevel;
  target_audience: TargetAudience;
  activity_type: ActivityType;
  notes: string;
  required_flags: string[];
  dependencies: string[]; // Step IDs that must be completed first
  is_active: boolean;
  notify_role_on_complete: NotifyRole;
  section: string; // 'onboarding' | 'hands_on'
  // Input config fields for data collection steps
  input_type: InputConfigType | '';
  input_label: string;
  input_accept: string;
  input_document_type: string;
  input_download_url: string;
}

const PROFILE_FLAGS = [
  { key: 'has_ca_dre', label: 'Has CA DRE License' },
  { key: 'works_in_office', label: 'Works in Office' },
  { key: 'has_mlo_license', label: 'Has MLO License' },
  { key: 'needs_laptop', label: 'Needs Company Laptop' },
  { key: 'remote_worker', label: 'Remote Worker' },
];

const INPUT_TYPE_OPTIONS: { value: InputConfigType; label: string; description: string }[] = [
  { value: 'file_upload', label: 'File Upload', description: 'User uploads a file (PDF, image, etc.)' },
  { value: 'phone_input', label: 'Phone Number', description: 'User enters their phone number' },
  { value: 'text_input', label: 'Text Input', description: 'User enters text data' },
  { value: 'w9', label: 'W9 Form Upload', description: 'User uploads completed W9 form' },
  { value: 'banking', label: 'Banking Info Upload', description: 'User uploads banking/wire instructions' },
];

const STORAGE_FOLDER_OPTIONS = [
  { value: 'headshots', label: 'Headshots' },
  { value: 'w9', label: 'W9 Forms' },
  { value: 'banking', label: 'Banking Documents' },
  { value: 'certificates', label: 'Certificates & Licenses' },
  { value: 'contracts', label: 'Contracts & Agreements' },
  { value: 'signatures', label: 'Signatures' },
  { value: 'documents', label: 'General Documents' },
];

const StepCard: React.FC<{
  step: OnboardingStep;
  onEdit: (step: OnboardingStep) => void;
  onToggleActive: (step: OnboardingStep) => void;
  onDelete: (step: OnboardingStep) => void;
}> = ({ step, onEdit, onToggleActive, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-3xl border border-slate-200 bg-[#f7f9fc] p-5 shadow-xl shadow-lg ${isDragging ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Step {step.sequential_order}</p>
          <h3 className="text-xl font-semibold text-slate-900 mt-1">{step.task_name}</h3>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${priorityColors[step.priority]}`}>
              {step.priority.toUpperCase()}
            </span>
            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${activityColors[step.activity_type]}`}>
              {activityTypeCopy[step.activity_type].label}
            </span>
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-white text-slate-900">
              {targetCopy[step.target_audience]}
            </span>
            {!step.is_active && (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-200">
                Paused
              </span>
            )}
          </div>
          {step.notes && <p className="text-sm text-slate-600 mt-4 whitespace-pre-line">{step.notes}</p>}
        </div>
        <button
          {...listeners}
          {...attributes}
          className="p-2 rounded-2xl bg-[#f7f9fc] text-slate-500 hover:text-slate-900 cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <Bars3BottomLeftIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={() => onEdit(step)}
          className="px-4 py-2 rounded-2xl bg-white text-slate-600 hover:text-slate-900 text-sm font-semibold border border-slate-200"
        >
          Edit
        </button>
        <button
          onClick={() => onToggleActive(step)}
          className={`px-4 py-2 rounded-2xl text-sm font-semibold ${
            step.is_active ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          }`}
        >
          {step.is_active ? 'Hide' : 'Activate'}
        </button>
        <button
          onClick={() => onDelete(step)}
          className="p-2 rounded-2xl bg-red-50 text-red-600 hover:text-red-700 border border-red-200"
          title="Delete step"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// Sortable table row for the "All Steps" view
const SortableTableRow: React.FC<{
  step: OnboardingStep;
  categoryName: string;
  selected?: boolean;
  onToggleSelect?: (stepId: string) => void;
  onEdit: (step: OnboardingStep) => void;
  onToggleActive: (step: OnboardingStep) => void;
  onDelete: (step: OnboardingStep) => void;
}> = ({ step, categoryName, selected, onToggleSelect, onEdit, onToggleActive, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b border-slate-100 hover:bg-slate-50 ${isDragging ? 'opacity-50 bg-blue-50' : ''} ${selected ? 'bg-blue-50/50' : ''}`}
    >
      {onToggleSelect && (
        <td className="px-3 py-3 w-8">
          <input
            type="checkbox"
            checked={selected || false}
            onChange={() => onToggleSelect(step.id)}
            className="h-4 w-4 rounded border-slate-300 text-lumin-primary focus:ring-lumin-primary/40"
          />
        </td>
      )}
      <td className="px-3 py-3 w-10">
        <button
          {...listeners}
          {...attributes}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <Bars3BottomLeftIcon className="h-4 w-4" />
        </button>
      </td>
      <td className="px-3 py-3 text-xs text-slate-500 font-mono">{step.sequential_order}</td>
      <td className="px-3 py-3">
        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">{categoryName}</span>
      </td>
      <td className="px-3 py-3 font-medium text-slate-900">{step.task_name}</td>
      <td className="px-3 py-3">
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${priorityColors[step.priority]}`}>
          {step.priority}
        </span>
      </td>
      <td className="px-3 py-3">
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${activityColors[step.activity_type]}`}>
          {activityTypeCopy[step.activity_type].label}
        </span>
      </td>
      <td className="px-3 py-3">
        <span className="text-xs text-slate-600">{targetCopy[step.target_audience]}</span>
      </td>
      <td className="px-3 py-3">
        {step.is_active ? (
          <span className="text-xs text-emerald-600">Active</span>
        ) : (
          <span className="text-xs text-amber-600">Hidden</span>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(step)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onToggleActive(step)}
            className={`p-1.5 rounded-lg ${
              step.is_active
                ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'
                : 'text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
            title={step.is_active ? 'Hide' : 'Activate'}
          >
            {step.is_active ? (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
          <button
            onClick={() => onDelete(step)}
            className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

type ViewMode = 'categories' | 'all';

const WorkflowBuilder: React.FC = () => {
  const [categories, setCategories] = useState<StepCategoryRecord[]>([]);
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('categories');

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<StepCategoryRecord | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryFormState>({
    name: '',
    slug: '',
    description: '',
    color: '#0F62FE',
    accent_color: '#9EAFFF',
    icon: 'Squares2X2Icon',
    is_active: true,
  });

  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState<OnboardingStep | null>(null);
  const [stepForm, setStepForm] = useState<StepFormState>({
    task_name: '',
    category: '',
    priority: 'medium',
    target_audience: 'loan_officer',
    activity_type: 'task',
    notes: '',
    required_flags: [],
    dependencies: [],
    is_active: true,
    notify_role_on_complete: 'none',
    section: 'onboarding',
    input_type: '',
    input_label: '',
    input_accept: '',
    input_document_type: '',
    input_download_url: '',
  });

  const [activeSection, setActiveSection] = useState<'onboarding' | 'hands_on'>('onboarding');
  const [selectedSteps, setSelectedSteps] = useState<Set<string>>(new Set());
  const [filterAudience, setFilterAudience] = useState<'all' | TargetAudience>('all');
  const [filterActivity, setFilterActivity] = useState<'all' | ActivityType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentStepPage, setCurrentStepPage] = useState(1);
  const [stepsPerPage, setStepsPerPage] = useState(50);

  const sensors = useSensors(useSensor(PointerSensor));

  const loadBuilderData = async () => {
    try {
      setLoading(true);
      const [categoryData, stepData] = await Promise.all([
        api.getStepCategories(true),
        api.getOnboardingSteps({ activeOnly: false }),
      ]);
      setCategories(categoryData);
      setSteps(stepData ?? []);
    } catch (error) {
      // Error handled silently
      alert('Failed to load workflow data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBuilderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sectionSteps = useMemo(() => {
    return steps.filter((step) => step.section === activeSection);
  }, [steps, activeSection]);

  const stepCountMap = useMemo(() => {
    return sectionSteps.reduce<Record<string, number>>((acc, step) => {
      acc[step.category] = (acc[step.category] || 0) + 1;
      return acc;
    }, {});
  }, [sectionSteps]);

  const activeCategory = categories.find((cat) => cat.slug === selectedCategory) || null;

  const filteredSteps = useMemo(() => {
    if (!activeCategory) return [];
    return sectionSteps
      .filter((step) => step.category === activeCategory.slug)
      .filter((step) => (filterAudience === 'all' ? true : step.target_audience === filterAudience))
      .filter((step) => (filterActivity === 'all' ? true : step.activity_type === filterActivity))
      .filter((step) => {
        if (!searchTerm) return true;
        const normalized = searchTerm.toLowerCase();
        return (
          step.task_name.toLowerCase().includes(normalized) ||
          (step.notes ? step.notes.toLowerCase().includes(normalized) : false)
        );
      })
      .sort((a, b) => a.sequential_order - b.sequential_order);
  }, [activeCategory, sectionSteps, filterAudience, filterActivity, searchTerm]);

  const totalStepPages = Math.ceil(filteredSteps.length / stepsPerPage) || 1;
  const paginatedSteps = filteredSteps.slice(
    (currentStepPage - 1) * stepsPerPage,
    currentStepPage * stepsPerPage
  );

  // All steps sorted by sequential_order for the "All Steps" table view
  const allStepsSorted = useMemo(() => {
    return sectionSteps
      .filter((step) => (filterAudience === 'all' ? true : step.target_audience === filterAudience))
      .filter((step) => (filterActivity === 'all' ? true : step.activity_type === filterActivity))
      .filter((step) => {
        if (!searchTerm) return true;
        const normalized = searchTerm.toLowerCase();
        return (
          step.task_name.toLowerCase().includes(normalized) ||
          (step.notes ? step.notes.toLowerCase().includes(normalized) : false)
        );
      })
      .sort((a, b) => a.sequential_order - b.sequential_order);
  }, [sectionSteps, filterAudience, filterActivity, searchTerm]);

  // Category name lookup for table view
  const categoryNameMap = useMemo(() => {
    return categories.reduce<Record<string, string>>((acc, cat) => {
      acc[cat.slug] = cat.name;
      return acc;
    }, {});
  }, [categories]);

  const openCategoryModal = (category?: StepCategoryRecord) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        color: category.color,
        accent_color: category.accent_color,
        icon: category.icon,
        is_active: category.is_active,
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        slug: '',
        description: '',
        color: '#0F62FE',
        accent_color: '#9EAFFF',
        icon: 'Squares2X2Icon',
        is_active: true,
      });
    }
    setShowCategoryModal(true);
  };

  const toggleRequiredFlag = (flagKey: string) => {
    setStepForm((prev) => ({
      ...prev,
      required_flags: prev.required_flags.includes(flagKey)
        ? prev.required_flags.filter((f) => f !== flagKey)
        : [...prev.required_flags, flagKey],
    }));
  };

  const toggleDependency = (stepId: string) => {
    setStepForm((prev) => ({
      ...prev,
      dependencies: prev.dependencies.includes(stepId)
        ? prev.dependencies.filter((id) => id !== stepId)
        : [...prev.dependencies, stepId],
    }));
  };

  const openStepModal = (categorySlug: string, step?: OnboardingStep) => {
    if (step) {
      setEditingStep(step);
      setStepForm({
        task_name: step.task_name,
        category: step.category,
        priority: step.priority,
        target_audience: step.target_audience,
        activity_type: step.activity_type,
        notes: step.notes || '',
        required_flags: step.required_flags || [],
        dependencies: step.dependencies || [],
        is_active: step.is_active,
        notify_role_on_complete: step.notify_role_on_complete || 'none',
        section: step.section || 'onboarding',
        input_type: step.input_config?.type || '',
        input_label: step.input_config?.label || '',
        input_accept: step.input_config?.accept || '',
        input_document_type: step.input_config?.documentType || '',
        input_download_url: step.input_config?.downloadUrl || '',
      });
    } else {
      setEditingStep(null);
      setStepForm({
        task_name: '',
        category: categorySlug,
        priority: 'medium',
        target_audience: 'loan_officer',
        activity_type: 'task',
        notes: '',
        required_flags: [],
        dependencies: [],
        is_active: true,
        notify_role_on_complete: 'none',
        section: activeSection,
        input_type: '',
        input_label: '',
        input_accept: '',
        input_document_type: '',
        input_download_url: '',
      });
    }
    setSelectedCategory(categorySlug);
    setShowStepModal(true);
  };

  const handleCategorySave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!categoryForm.name.trim()) {
      alert('Name is required');
      return;
    }

    try {
      if (editingCategory) {
        await api.updateStepCategory(editingCategory.id, {
          name: categoryForm.name,
          description: categoryForm.description || null,
          color: categoryForm.color,
          accent_color: categoryForm.accent_color,
          icon: categoryForm.icon,
          is_active: categoryForm.is_active,
        });
      } else {
        await api.createStepCategory({
          name: categoryForm.name,
          slug: categoryForm.slug || slugify(categoryForm.name),
          description: categoryForm.description || null,
          color: categoryForm.color,
          accent_color: categoryForm.accent_color,
          icon: categoryForm.icon,
          sequence_order: (categories.length + 1) * 1000,
        });
      }
      setShowCategoryModal(false);
      await loadBuilderData();
    } catch (error) {
      // Error handled silently
      alert('Unable to save category.');
    }
  };

  const handleStepSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stepForm.task_name.trim()) {
      alert('Step name required');
      return;
    }
    if (!stepForm.category) {
      alert('Category required');
      return;
    }

    try {
      const targetCategory = categories.find((cat) => cat.slug === stepForm.category);
      if (!targetCategory) {
        alert('Invalid category');
        return;
      }

      // Build input_config if an input type is selected
      let inputConfig: InputConfig | null = null;
      if (stepForm.input_type) {
        inputConfig = {
          type: stepForm.input_type as InputConfigType,
          label: stepForm.input_label || undefined,
          accept: stepForm.input_accept || undefined,
          documentType: stepForm.input_document_type || undefined,
          downloadUrl: stepForm.input_download_url || undefined,
        };
      }

      const baseOrder = (categories.findIndex((cat) => cat.slug === stepForm.category) + 1) * 1000;
      const categorySteps = steps.filter((step) => step.category === stepForm.category);

      if (editingStep) {
        await api.updateOnboardingStep(editingStep.id, {
          category: stepForm.category,
          task_name: stepForm.task_name,
          priority: stepForm.priority,
          target_audience: stepForm.target_audience,
          activity_type: stepForm.activity_type,
          notes: stepForm.notes || null,
          required_flags: stepForm.required_flags.length > 0 ? stepForm.required_flags : null,
          dependencies: stepForm.dependencies.length > 0 ? stepForm.dependencies : null,
          is_active: stepForm.is_active,
          notify_role_on_complete: stepForm.notify_role_on_complete,
          section: stepForm.section,
          input_config: inputConfig,
        });
      } else {
        await api.createOnboardingStep({
          category: stepForm.category,
          task_name: stepForm.task_name,
          priority: stepForm.priority,
          target_audience: stepForm.target_audience,
          activity_type: stepForm.activity_type,
          notes: stepForm.notes || null,
          required_flags: stepForm.required_flags.length > 0 ? stepForm.required_flags : null,
          dependencies: stepForm.dependencies.length > 0 ? stepForm.dependencies : null,
          is_active: stepForm.is_active,
          notify_role_on_complete: stepForm.notify_role_on_complete,
          section: stepForm.section,
          sequential_order: baseOrder + categorySteps.length + 1,
          input_config: inputConfig,
        });
      }

      setShowStepModal(false);
      await loadBuilderData();
    } catch (error) {
      // Error handled silently
      alert('Unable to save step.');
    }
  };

  const applyStepOrderUpdates = (updates: { id: string; sequential_order: number }[]) => {
    setSteps((prev) =>
      prev.map((step) => {
        const update = updates.find((item) => item.id === step.id);
        return update ? { ...step, sequential_order: update.sequential_order } : step;
      })
    );
  };

  const handleStepDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !activeCategory) return;

    // Get all steps in this category (not filtered), sorted by current order
    const categorySteps = steps
      .filter((step) => step.category === activeCategory.slug)
      .sort((a, b) => a.sequential_order - b.sequential_order);

    const activeIndex = categorySteps.findIndex((step) => step.id === active.id);
    const overIndex = categorySteps.findIndex((step) => step.id === over.id);
    if (activeIndex === -1 || overIndex === -1) return;

    const reordered = arrayMove(categorySteps, activeIndex, overIndex);
    const baseOrder = (categories.findIndex((cat) => cat.slug === activeCategory.slug) + 1) * 1000;
    const updates = reordered.map((step, index) => ({
      id: step.id,
      sequential_order: baseOrder + index + 1,
    }));

    applyStepOrderUpdates(updates);
    try {
      setSavingOrder(true);
      await api.bulkUpdateStepOrder(updates);
    } catch (error) {
      // Error handled silently
      alert('Failed to update order.');
      loadBuilderData();
    } finally {
      setSavingOrder(false);
    }
  };

  // Handle drag-end for the "All Steps" table view (global reordering)
  const handleAllStepsDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Get all steps sorted by current order
    const sortedSteps = [...steps].sort((a, b) => a.sequential_order - b.sequential_order);

    const activeIndex = sortedSteps.findIndex((step) => step.id === active.id);
    const overIndex = sortedSteps.findIndex((step) => step.id === over.id);
    if (activeIndex === -1 || overIndex === -1) return;

    const reordered = arrayMove(sortedSteps, activeIndex, overIndex);

    // Reassign sequential_order globally (1, 2, 3, ...)
    const updates = reordered.map((step, index) => ({
      id: step.id,
      sequential_order: index + 1,
    }));

    applyStepOrderUpdates(updates);
    try {
      setSavingOrder(true);
      await api.bulkUpdateStepOrder(updates);
    } catch (error) {
      alert('Failed to update order.');
      loadBuilderData();
    } finally {
      setSavingOrder(false);
    }
  };

  const syncAllStepOrders = async (updatedCategories: StepCategoryRecord[]) => {
    const updates: { id: string; sequential_order: number }[] = [];
    updatedCategories.forEach((cat, catIndex) => {
      const baseOrder = (catIndex + 1) * 1000;
      const catSteps = steps
        .filter((step) => step.category === cat.slug)
        .sort((a, b) => a.sequential_order - b.sequential_order);
      catSteps.forEach((step, idx) => {
        updates.push({ id: step.id, sequential_order: baseOrder + idx + 1 });
      });
    });
    applyStepOrderUpdates(updates);
    if (updates.length) {
      await api.bulkUpdateStepOrder(updates);
    }
  };

  const moveCategory = async (categoryId: string, direction: 'up' | 'down') => {
    const currentIndex = categories.findIndex((cat) => cat.id === categoryId);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex === -1 || targetIndex < 0 || targetIndex >= categories.length) return;

    const reordered = arrayMove(categories, currentIndex, targetIndex).map((cat, index) => ({
      ...cat,
      sequence_order: (index + 1) * 1000,
    }));
    setCategories(reordered);
    try {
      await api.updateCategoryOrder(reordered.map((cat) => ({ id: cat.id, sequence_order: cat.sequence_order })));
      await syncAllStepOrders(reordered);
    } catch (error) {
      // Error handled silently
      alert('Unable to reorder categories.');
      loadBuilderData();
    }
  };

  const toggleCategoryActive = async (category: StepCategoryRecord) => {
    try {
      await api.updateStepCategory(category.id, { is_active: !category.is_active });
      await loadBuilderData();
    } catch (error) {
      // Error handled silently
      alert('Unable to update category.');
    }
  };

  const toggleStepActive = async (step: OnboardingStep) => {
    try {
      await api.updateOnboardingStep(step.id, { is_active: !step.is_active });
      await loadBuilderData();
    } catch (error) {
      // Error handled silently
      alert('Unable to update step.');
    }
  };

  const deleteStep = async (step: OnboardingStep) => {
    if (!window.confirm('Delete this step from all workflows?')) return;
    try {
      await api.deleteOnboardingStep(step.id);
      await loadBuilderData();
    } catch (error) {
      // Error handled silently
      alert('Unable to delete step.');
    }
  };

  const deleteCategory = async (category: StepCategoryRecord) => {
    const stepCount = stepCountMap[category.slug] || 0;
    const message = stepCount > 0
      ? `Delete "${category.name}" and all ${stepCount} steps in this category?`
      : `Delete "${category.name}"?`;

    if (!window.confirm(message)) return;

    try {
      await api.deleteStepCategory(category.id);
      if (selectedCategory === category.slug) {
        setSelectedCategory(null);
      }
      await loadBuilderData();
    } catch (error) {
      // Error handled silently
      alert('Unable to delete category.');
    }
  };

  return (
    <AdminShell
      title="Step Management"
      description="Shape categories, reorder sequences, and label every activity so the team knows exactly what is expected."
      nav={adminNavigation}
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => openCategoryModal()}
            className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-xl text-sm font-semibold transition"
          >
            + Category
          </button>
          <button
            onClick={() => openStepModal(activeCategory?.slug || categories[0]?.slug || '')}
            className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-xl text-sm font-semibold transition"
          >
            + Step
          </button>
        </div>
      }
    >
      {/* Section toggle + View mode toggle */}
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="flex items-center bg-slate-100 rounded-2xl p-1">
          <button
            onClick={() => { setActiveSection('onboarding'); setSelectedSteps(new Set()); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
              activeSection === 'onboarding' ? 'bg-lumin-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Onboarding
          </button>
          <button
            onClick={() => { setActiveSection('hands_on'); setSelectedSteps(new Set()); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
              activeSection === 'hands_on' ? 'bg-lumin-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Hands On
          </button>
        </div>

        <div className="flex items-center bg-slate-100 rounded-2xl p-1">
          <button
            onClick={() => setViewMode('categories')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition ${
              viewMode === 'categories' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Squares2X2Icon className="h-4 w-4" />
            Categories
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition ${
              viewMode === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <TableCellsIcon className="h-4 w-4" />
            All Steps
          </button>
        </div>

        {selectedSteps.size > 0 && (
          <button
            onClick={async () => {
              const targetSection = activeSection === 'onboarding' ? 'hands_on' : 'onboarding';
              const label = targetSection === 'hands_on' ? 'Hands On' : 'Onboarding';
              if (!window.confirm(`Move ${selectedSteps.size} step(s) to ${label}?`)) return;
              try {
                await api.bulkUpdateStepSection(Array.from(selectedSteps), targetSection);
                setSelectedSteps(new Set());
                await loadBuilderData();
              } catch {
                alert('Failed to move steps.');
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-2xl text-sm transition"
          >
            Move {selectedSteps.size} to {activeSection === 'onboarding' ? 'Hands On' : 'Onboarding'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-600">
          <div className="h-12 w-12 border-2 border-slate-200 border-t-lumin-accent rounded-full animate-spin" />
        </div>
      ) : viewMode === 'all' ? (
        /* ===== ALL STEPS TABLE VIEW ===== */
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Search steps..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 rounded-2xl bg-white text-sm text-slate-600 border border-slate-200 focus:outline-none focus:border-lumin-primary/40 w-64"
            />
            <select
              value={filterAudience}
              onChange={(e) => setFilterAudience(e.target.value as any)}
              className="px-4 py-2 rounded-2xl bg-white text-sm text-slate-600 border border-slate-200"
            >
              <option value="all">All audiences</option>
              <option value="loan_officer">Loan Officer</option>
              <option value="admin">Admin</option>
              <option value="va">VA</option>
            </select>
            <select
              value={filterActivity}
              onChange={(e) => setFilterActivity(e.target.value as any)}
              className="px-4 py-2 rounded-2xl bg-white text-sm text-slate-600 border border-slate-200"
            >
              <option value="all">All types</option>
              {Object.entries(activityTypeCopy).map(([key, value]) => (
                <option value={key} key={key}>{value.label}</option>
              ))}
            </select>
            <div className="ml-auto text-sm text-slate-500">
              {allStepsSorted.length} steps {savingOrder && <span className="text-lumin-primary">(Saving...)</span>}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleAllStepsDragEnd}>
              <SortableContext items={allStepsSorted.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-3 w-8">
                        <input
                          type="checkbox"
                          checked={allStepsSorted.length > 0 && selectedSteps.size === allStepsSorted.length}
                          onChange={() => {
                            if (selectedSteps.size === allStepsSorted.length) {
                              setSelectedSteps(new Set());
                            } else {
                              setSelectedSteps(new Set(allStepsSorted.map((s) => s.id)));
                            }
                          }}
                          className="h-4 w-4 rounded border-slate-300 text-lumin-primary focus:ring-lumin-primary/40"
                        />
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-10"></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">Order</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Step Name</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">Priority</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-28">Type</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">Audience</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">Status</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allStepsSorted.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-3 py-12 text-center text-slate-500">
                          No steps match the current filters.
                        </td>
                      </tr>
                    ) : (
                      allStepsSorted.map((step) => (
                        <SortableTableRow
                          key={step.id}
                          step={step}
                          categoryName={categoryNameMap[step.category] || step.category}
                          selected={selectedSteps.has(step.id)}
                          onToggleSelect={(id) => {
                            setSelectedSteps((prev) => {
                              const next = new Set(prev);
                              if (next.has(id)) next.delete(id);
                              else next.add(id);
                              return next;
                            });
                          }}
                          onEdit={(s) => openStepModal(s.category, s)}
                          onToggleActive={toggleStepActive}
                          onDelete={deleteStep}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      ) : (
        /* ===== CATEGORY VIEW (Original) ===== */
        <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-8">
          <section className="space-y-4">
            {categories.map((category, index) => (
              <div
                key={category.id}
                className={`rounded-3xl border p-5 cursor-pointer transition-all ${
                  selectedCategory === category.slug
                    ? 'border-lumin-primary/30 bg-white shadow-xl shadow-lg'
                    : 'border-slate-200 bg-[#f7f9fc] hover:border-lumin-primary/30'
                }`}
                onClick={() => setSelectedCategory(category.slug)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Sequence {index + 1}</p>
                    <h3 className="text-lg font-semibold text-slate-900 mt-1">{category.name}</h3>
                    {category.description && (
                      <p className="text-xs text-slate-500 mt-1">{category.description}</p>
                    )}
                  </div>
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      openCategoryModal(category);
                    }}
                    className="p-2 rounded-2xl bg-[#f7f9fc] text-slate-400 hover:text-slate-900"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
                  <span>{stepCountMap[category.slug] || 0} steps</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        moveCategory(category.id, 'up');
                      }}
                      className="p-2 rounded-2xl bg-[#f7f9fc] text-slate-400 hover:text-slate-900"
                      disabled={index === 0}
                    >
                      <ArrowUpIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        moveCategory(category.id, 'down');
                      }}
                      className="p-2 rounded-2xl bg-[#f7f9fc] text-slate-400 hover:text-slate-900"
                      disabled={index === categories.length - 1}
                    >
                      <ArrowDownIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleCategoryActive(category);
                      }}
                      className={`px-3 py-1 rounded-2xl text-xs font-semibold border ${
                        category.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-600 border-slate-200'
                      }`}
                    >
                      {category.is_active ? 'Active' : 'Hidden'}
                    </button>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteCategory(category);
                      }}
                      className="p-2 rounded-2xl bg-red-50 text-red-600 hover:text-red-700 border border-red-200"
                      title="Delete category"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </section>

          <section className="space-y-6">
            {activeCategory ? (
              <>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <p className="text-sm uppercase tracking-[0.4em] text-slate-400">{activeCategory.name}</p>
                    <h2 className="text-3xl font-semibold text-slate-900">{stepCountMap[activeCategory.slug] || 0} steps</h2>
                    {savingOrder && (
                      <p className="text-xs text-slate-500 mt-1">Saving new sequence...</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <input
                      type="text"
                      placeholder="Search steps"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      className="px-4 py-2 rounded-2xl bg-white text-sm text-slate-600 border border-slate-200 focus:outline-none focus:border-lumin-primary/40"
                    />
                    <select
                      value={filterAudience}
                      onChange={(event) => setFilterAudience(event.target.value as any)}
                      className="px-4 py-2 rounded-2xl bg-white text-sm text-slate-600 border border-slate-200"
                    >
                      <option value="all">All audiences</option>
                      <option value="loan_officer">Loan Officer</option>
                      <option value="admin">Admin</option>
                      <option value="va">VA</option>
                    </select>
                    <select
                      value={filterActivity}
                      onChange={(event) => setFilterActivity(event.target.value as any)}
                      className="px-4 py-2 rounded-2xl bg-white text-sm text-slate-600 border border-slate-200"
                    >
                      <option value="all">All activity types</option>
                      {Object.entries(activityTypeCopy).map(([key, value]) => (
                        <option value={key} key={key}>
                          {value.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStepDragEnd}>
                  <SortableContext items={paginatedSteps.map((step) => step.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-4">
                      {paginatedSteps.length === 0 ? (
                        <div className="text-center text-slate-500 border border-dashed border-slate-200 rounded-3xl py-16">
                          No steps match the current filters.
                        </div>
                      ) : (
                        paginatedSteps.map((step) => (
                          <StepCard
                            key={step.id}
                            step={step}
                            onEdit={(item) => openStepModal(item.category, item)}
                            onToggleActive={toggleStepActive}
                            onDelete={deleteStep}
                          />
                        ))
                      )}
                    </div>
                  </SortableContext>
                </DndContext>

                {filteredSteps.length > 0 && (
                  <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                    <p className="text-sm text-slate-600">
                      {filteredSteps.length} {filteredSteps.length === 1 ? 'step' : 'steps'}
                    </p>
                    {/* Only show pagination controls when there are enough items to paginate */}
                    {filteredSteps.length > PAGE_SIZE_OPTIONS[0] && (
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">Show:</span>
                          <select
                            value={stepsPerPage}
                            onChange={(e) => {
                              setStepsPerPage(Number(e.target.value));
                              setCurrentStepPage(1);
                            }}
                            className="px-3 py-1.5 rounded-xl bg-white text-sm text-slate-600 border border-slate-200"
                          >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                        </div>
                        {totalStepPages > 1 && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setCurrentStepPage((prev) => Math.max(1, prev - 1))}
                              disabled={currentStepPage === 1}
                              className="inline-flex items-center gap-1 px-3 py-2 rounded-2xl bg-white border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ChevronLeftIcon className="h-4 w-4" />
                              Previous
                            </button>
                            <span className="text-sm text-slate-600">
                              Page {currentStepPage} of {totalStepPages}
                            </span>
                            <button
                              onClick={() => setCurrentStepPage((prev) => Math.min(totalStepPages, prev + 1))}
                              disabled={currentStepPage === totalStepPages}
                              className="inline-flex items-center gap-1 px-3 py-2 rounded-2xl bg-white border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                              <ChevronRightIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-[#f7f9fc] py-16 text-center text-slate-500">
                Select a category to start editing steps.
              </div>
            )}
          </section>
        </div>
      )}

      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={() => setShowCategoryModal(false)}>
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-xl p-8" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-semibold text-slate-900">
                {editingCategory ? 'Edit category' : 'New category'}
              </h3>
              <button onClick={() => setShowCategoryModal(false)} className="text-slate-500 hover:text-slate-900">✕</button>
            </div>
            <form className="space-y-4" onSubmit={handleCategorySave}>
              <div>
                <label className="text-sm text-slate-500">Name</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(event) =>
                    setCategoryForm((prev) => ({
                      ...prev,
                      name: event.target.value,
                      slug: editingCategory ? prev.slug : slugify(event.target.value),
                    }))
                  }
                  className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                  required
                />
              </div>
              {!editingCategory && (
                <div>
                  <label className="text-sm text-slate-500">Slug</label>
                  <input
                    type="text"
                    value={categoryForm.slug}
                    onChange={(event) => setCategoryForm((prev) => ({ ...prev, slug: slugify(event.target.value) }))}
                    className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                    required
                  />
                </div>
              )}
              <div>
                <label className="text-sm text-slate-500">Description</label>
                <textarea
                  value={categoryForm.description}
                  onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))}
                  className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={categoryForm.is_active}
                  onChange={(event) => setCategoryForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                  className="h-4 w-4"
                />
                <span className="text-sm text-slate-600">Active</span>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 bg-lumin-primary text-white font-semibold rounded-2xl py-2">
                  {editingCategory ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  className="flex-1 bg-white text-slate-600 font-semibold rounded-2xl py-2 border border-slate-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showStepModal && activeCategory && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={() => setShowStepModal(false)}>
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-4xl p-8 max-h-[90vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-slate-400">{activeCategory.name}</p>
                <h3 className="text-2xl font-semibold text-slate-900">
                  {editingStep ? 'Edit step' : 'New step'}
                </h3>
              </div>
              <button onClick={() => setShowStepModal(false)} className="text-slate-500 hover:text-slate-900">✕</button>
            </div>
            <form className="space-y-4" onSubmit={handleStepSave}>
              {/* Two-column layout for main fields */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left column */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-slate-500">Name</label>
                    <input
                      type="text"
                      value={stepForm.task_name}
                      onChange={(event) => setStepForm((prev) => ({ ...prev, task_name: event.target.value }))}
                      className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-500">Category</label>
                    <select
                      value={stepForm.category}
                      onChange={(event) => setStepForm((prev) => ({ ...prev, category: event.target.value }))}
                      className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                      required
                    >
                      <option value="">Select a category...</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.slug}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-500">Section</label>
                    <select
                      value={stepForm.section}
                      onChange={(event) => setStepForm((prev) => ({ ...prev, section: event.target.value }))}
                      className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                    >
                      <option value="onboarding">Onboarding</option>
                      <option value="hands_on">Hands On</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm text-slate-500">Priority</label>
                      <select
                        value={stepForm.priority}
                        onChange={(event) => setStepForm((prev) => ({ ...prev, priority: event.target.value as PriorityLevel }))}
                        className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-slate-500">Audience</label>
                      <select
                        value={stepForm.target_audience}
                        onChange={(event) => setStepForm((prev) => ({ ...prev, target_audience: event.target.value as TargetAudience }))}
                        className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                      >
                        <option value="loan_officer">LO</option>
                        <option value="admin">Admin</option>
                        <option value="va">VA</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-slate-500">Type</label>
                      <select
                        value={stepForm.activity_type}
                        onChange={(event) => setStepForm((prev) => ({ ...prev, activity_type: event.target.value as ActivityType }))}
                        className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                      >
                        {Object.entries(activityTypeCopy).map(([key, value]) => (
                          <option key={key} value={key}>
                            {value.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-500">Notify on Complete</label>
                    <select
                      value={stepForm.notify_role_on_complete}
                      onChange={(event) => setStepForm((prev) => ({ ...prev, notify_role_on_complete: event.target.value as NotifyRole }))}
                      className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                    >
                      {Object.entries(notifyRoleLabels).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-500">Notes / instructions</label>
                    <textarea
                      value={stepForm.notes}
                      onChange={(event) => setStepForm((prev) => ({ ...prev, notes: event.target.value }))}
                      className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                      rows={4}
                    />
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-4">

              {/* Data Collection / Input Config Section */}
              <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50">
                <label className="text-sm font-semibold text-slate-700 mb-2 block">
                  Data Collection (Optional)
                </label>
                <p className="text-xs text-slate-500 mb-3">
                  Require the user to submit data before completing this step.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-slate-500">Input Type</label>
                    <select
                      value={stepForm.input_type}
                      onChange={(event) => {
                        const newType = event.target.value as InputConfigType | '';
                        // Set smart defaults based on type
                        let defaults = { input_accept: '', input_document_type: 'documents', input_label: '', input_download_url: '' };
                        if (newType === 'file_upload') {
                          defaults = { input_accept: '.jpg,.jpeg,.png,.pdf', input_document_type: 'documents', input_label: 'Upload File', input_download_url: '' };
                        } else if (newType === 'w9') {
                          defaults = { input_accept: '.pdf', input_document_type: 'w9', input_label: 'Upload W9 Form', input_download_url: '/forms/w9-blank.pdf' };
                        } else if (newType === 'banking') {
                          defaults = { input_accept: '.pdf', input_document_type: 'banking', input_label: 'Upload Banking Info', input_download_url: '/forms/banking-wire-instructions.pdf' };
                        } else if (newType === 'phone_input') {
                          defaults = { input_accept: '', input_document_type: '', input_label: 'Enter Phone Number', input_download_url: '' };
                        }
                        setStepForm((prev) => ({ ...prev, input_type: newType, ...defaults }));
                      }}
                      className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                    >
                      <option value="">None - No data collection</option>
                      {INPUT_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {stepForm.input_type && (
                      <p className="text-xs text-slate-400 mt-1">
                        {INPUT_TYPE_OPTIONS.find((o) => o.value === stepForm.input_type)?.description}
                      </p>
                    )}
                  </div>

                  {stepForm.input_type && (
                    <>
                      <div>
                        <label className="text-xs text-slate-500">Label</label>
                        <input
                          type="text"
                          value={stepForm.input_label}
                          onChange={(event) => setStepForm((prev) => ({ ...prev, input_label: event.target.value }))}
                          placeholder="e.g., Upload Your Headshot"
                          className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                        />
                      </div>

                      {['file_upload', 'w9', 'banking'].includes(stepForm.input_type) && (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-slate-500">Accepted Files</label>
                              <input
                                type="text"
                                value={stepForm.input_accept}
                                onChange={(event) => setStepForm((prev) => ({ ...prev, input_accept: event.target.value }))}
                                placeholder=".jpg,.png,.pdf"
                                className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Storage Folder</label>
                              <select
                                value={stepForm.input_document_type}
                                onChange={(event) => setStepForm((prev) => ({ ...prev, input_document_type: event.target.value }))}
                                className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                              >
                                {STORAGE_FOLDER_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Blank Form Download URL (Optional)</label>
                            <input
                              type="text"
                              value={stepForm.input_download_url}
                              onChange={(event) => setStepForm((prev) => ({ ...prev, input_download_url: event.target.value }))}
                              placeholder="/forms/blank-form.pdf"
                              className="w-full mt-1 px-4 py-2 rounded-2xl bg-white text-slate-900 border border-slate-200"
                            />
                            <p className="text-xs text-slate-400 mt-1">If provided, user can download a blank form to fill out</p>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Required Flags in right column */}
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-2 block">
                  Required Flags (Optional)
                </label>
                <p className="text-xs text-slate-500 mb-2">
                  LOs must have these flags for task to be visible.
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {PROFILE_FLAGS.map((flag) => (
                    <label key={flag.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={stepForm.required_flags.includes(flag.key)}
                        onChange={() => toggleRequiredFlag(flag.key)}
                        className="h-4 w-4 rounded border-slate-300 text-lumin-primary focus:ring-lumin-primary/40"
                      />
                      <span className="text-xs text-slate-700">{flag.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Dependencies */}
              <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50">
                <label className="text-sm font-semibold text-slate-700 mb-2 block">
                  Dependencies (Optional)
                </label>
                <p className="text-xs text-slate-500 mb-3">
                  This task can't be started until the selected tasks are completed.
                </p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {steps
                    .filter((s) => s.id !== editingStep?.id) // Can't depend on itself
                    .sort((a, b) => a.sequential_order - b.sequential_order)
                    .map((step) => (
                      <label key={step.id} className="flex items-center gap-2 cursor-pointer py-1">
                        <input
                          type="checkbox"
                          checked={stepForm.dependencies.includes(step.id)}
                          onChange={() => toggleDependency(step.id)}
                          className="h-4 w-4 rounded border-slate-300 text-lumin-primary focus:ring-lumin-primary/40"
                        />
                        <span className="text-xs text-slate-700 flex-1 truncate">{step.task_name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          step.target_audience === 'admin' ? 'bg-purple-100 text-purple-700' :
                          step.target_audience === 'va' ? 'bg-blue-100 text-blue-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {targetCopy[step.target_audience]}
                        </span>
                      </label>
                    ))}
                </div>
                {stepForm.dependencies.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    {stepForm.dependencies.length} dependency selected
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={stepForm.is_active}
                  onChange={(event) => setStepForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                  className="h-4 w-4"
                />
                <span className="text-sm text-slate-600">Active</span>
              </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 bg-lumin-primary text-white font-semibold rounded-2xl py-2">
                  {editingStep ? 'Update step' : 'Create step'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowStepModal(false)}
                  className="flex-1 bg-white text-slate-600 font-semibold rounded-2xl py-2 border border-slate-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default WorkflowBuilder;
